import { License } from "@/types-games";

/**
 * License features that can be checked
 * These match the fields in the License interface
 */
export type LicenseFeature = keyof Omit<License, 'id' | 'name'>;

/**
 * Route configuration with required license features
 * Maps route paths to the license features they require
 */
export interface RouteConfig {
  path: string;
  requiredFeature: LicenseFeature | null; // null means no specific feature required (always accessible)
  description: string;
}

/**
 * Complete route-to-license mapping
 * This defines which license feature is needed for each route
 */
export const routeLicenseConfig: RouteConfig[] = [
  // Always accessible routes (no license check)
  { path: '/home', requiredFeature: null, description: 'Home Dashboard' },
  { path: '/settings', requiredFeature: null, description: 'Settings' },
  { path: '/tutorial', requiredFeature: null, description: 'Tutorial' },
  { path: '/documentation', requiredFeature: null, description: 'Documentation' },
  
  // Library routes
  { path: '/library', requiredFeature: 'library', description: 'Library Page' },
  { path: '/library/:itemId', requiredFeature: 'library', description: 'Library Item Detail' },
  
  // Template routes
  { path: '/templates', requiredFeature: 'templates', description: 'Templates Page' },
  { path: '/templates/:itemId', requiredFeature: 'templates', description: 'Template Detail' },
  
  // Document routes
  { path: '/documents', requiredFeature: 'documents', description: 'Documents Page' },
  { path: '/documents/:documentId', requiredFeature: 'documents', description: 'Document Detail' },
  
  // Dialog routes
  { path: '/dialogs', requiredFeature: 'dialogs', description: 'Dialogs Page' },
  
  // TMS routes
  { path: '/tms', requiredFeature: 'tms', description: 'Translation Management System' },
  { path: '/text-management', requiredFeature: 'tms', description: 'Text Management' },
  
  // Types and Tags routes
  { path: '/types-and-tags', requiredFeature: 'types', description: 'Types and Tags' },
  
  // Variables routes
  { path: '/variables', requiredFeature: 'variables', description: 'Variables' },
  { path: '/variables/:itemId', requiredFeature: 'variables', description: 'Variable Detail' },
  
  // Images routes
  { path: '/images', requiredFeature: 'images', description: 'Images' },
  
  // UI routes
  { path: '/ui', requiredFeature: 'ui', description: 'UI Elements' },
  
  // Relationships routes
  { path: '/relationship', requiredFeature: 'relationships', description: 'Relationships' },
  
  // Logic routes
  { path: '/logic', requiredFeature: 'logic', description: 'Visual Logic Flow' },
  { path: '/logic/:flowId', requiredFeature: 'logic', description: 'Logic Flow Detail' },
  
  // Context routes (if you have any)
  // { path: '/context', requiredFeature: 'context', description: 'Context Management' },
];

/**
 * Get the required license feature for a given path
 * Handles dynamic routes (e.g., /library/:itemId)
 */
export function getRequiredFeatureForPath(pathname: string): LicenseFeature | null {
  // First try exact match
  const exactMatch = routeLicenseConfig.find(config => config.path === pathname);
  if (exactMatch) {
    return exactMatch.requiredFeature;
  }
  
  // Then try pattern matching for dynamic routes
  for (const config of routeLicenseConfig) {
    if (config.path.includes(':')) {
      // Convert route pattern to regex (e.g., /library/:itemId -> /library/[^/]+)
      const pattern = config.path.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${pattern}$`);
      if (regex.test(pathname)) {
        return config.requiredFeature;
      }
    }
  }
  
  // No specific feature required
  return null;
}

/**
 * Check if a company's license allows access to a specific feature
 */
export function hasLicenseAccess(license: License | undefined, feature: LicenseFeature | null): boolean {
  // No feature required means always accessible
  if (feature === null) {
    return true;
  }
  
  // No license means no access to any feature
  if (!license) {
    return false;
  }
  
  // Check if the license has the required feature enabled
  return license[feature] === true;
}

/**
 * Get feature display name for UI messages
 */
export function getFeatureDisplayName(feature: LicenseFeature): string {
  const displayNames: Record<LicenseFeature, string> = {
    library: 'Library',
    templates: 'Templates',
    documents: 'Documents',
    dialogs: 'Dialogs',
    tms: 'Translation Management System',
    types: 'Types & Tags',
    variables: 'Variables',
    globals: 'Global Settings',
    images: 'Images',
    ui: 'UI Elements',
    relationships: 'Relationships',
    context: 'Context Management',
    logic: 'Visual Logic Flow',
  };
  
  return displayNames[feature] || feature;
}

